import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import Html from 'react-pdf-html';

import {
  styles,
  Header,
  Footer,
  GrayLine,
  StandardPDFColors,
} from './common/CommonPDFComponents';

import InformedIndividualSectionFactory from '../../../components/customer/reports/finalReport/reportDialogs/informedIndividualsSection/InformedIndividualSectionFactory';
import { fieldTypes } from '../../../components/customer/settings/finalReport/templateDialogs/common/fieldTypes';
import { getCurrentIntl } from '../../../i18n/intl/intl';
import { FinalReportDocumentField } from '../../../interfaces/FinalReportDocumentField';
import { ModifiedFinalReportDocument } from '../../../interfaces/ModifiedFinalReportDocument';

interface ReportInformationProps {
  assignee: string;
  reportType: string;
  reportSeverity: string;
  reportReference: string;
  dateReceived: string | null;
}

interface FieldInformation {
  content: string;
  title: string;
}

interface ReportTitleProps {
  title: string;
}

interface ReportCustomFields {
  fields: FinalReportDocumentField[];
}

const documentStyles = StyleSheet.create({
  detailsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: '23px',
  },
  updatedStatusContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '20px',
  },
  updatedStatus: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  information: {
    ...styles.sizeBody,
    marginTop: 16,
    gap: '4px',
    textTransform: 'uppercase',
  },
  reportTitle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  reportDefaultFieldDetails: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    columnGap: 12,
  },
  reportDefaultFieldNames: {
    flexDirection: 'column',
    textTransform: 'capitalize',
    rowGap: 4,
    color: '#4F4F4F',
  },
  reportDefaultFieldValues: {
    flexDirection: 'column',
    textTransform: 'uppercase',
    rowGap: 4,
    ...styles.medium,
  },
  reportCustomFieldSiblings: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

const formattedTextStyles = {
  h1: {
    fontSize: '28px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  h2: {
    fontSize: '21px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  h3: {
    fontSize: '16px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  h4: {
    fontSize: '14px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: '12px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '9px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  p: {
    margin: 0,
    lineHeight: 1.5,
    color: StandardPDFColors.gray,
  },
  ul: {
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  ol: {
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
};

const ReportDetailsHeader = () => {
  const intl = getCurrentIntl();
  return (
    <>
      <View style={documentStyles.detailsHeader}>
        <Text style={styles.sizeSubHeading}>
          {intl.formatMessage({ defaultMessage: 'Details' })}
        </Text>
        <View style={styles.spacer} />
      </View>
      <View style={{ marginTop: 6 }}>
        <GrayLine />
      </View>
    </>
  );
};

const ReportInformation = ({
  assignee,
  reportType,
  reportSeverity,
  reportReference,
  dateReceived,
}: ReportInformationProps) => {
  const intl = getCurrentIntl();
  return (
    <View style={documentStyles.information}>
      <View style={documentStyles.reportDefaultFieldDetails}>
        <View style={documentStyles.reportDefaultFieldNames}>
          {assignee && (
            <Text>
              {intl.formatMessage({ defaultMessage: 'Case Manager:' })}
            </Text>
          )}
          {dateReceived && (
            <Text>
              {intl.formatMessage({ defaultMessage: 'Date Received:' })}
            </Text>
          )}
          {reportType && (
            <Text>
              {intl.formatMessage({ defaultMessage: 'Report Type:' })}
            </Text>
          )}
          {reportReference && (
            <Text>
              {intl.formatMessage({ defaultMessage: 'Report Reference:' })}
            </Text>
          )}
          {reportSeverity && (
            <Text>
              {intl.formatMessage({ defaultMessage: 'Report Severity:' })}
            </Text>
          )}
        </View>
        <View style={documentStyles.reportDefaultFieldValues}>
          {assignee && <Text>{assignee}</Text>}
          {dateReceived && <Text>{dateReceived}</Text>}
          {reportType && <Text>{reportType}</Text>}
          {reportReference && <Text>{reportReference}</Text>}
          {reportSeverity && <Text>{reportSeverity}</Text>}
        </View>
      </View>
    </View>
  );
};

const BigTextField = ({ content, title }: FieldInformation) => (
  <View style={{ marginTop: 20, gap: '8px' }} wrap={true}>
    <Text style={{ ...styles.sizeBody, ...styles.medium }}>{title}</Text>
    <Html
      stylesheet={formattedTextStyles}
      style={{
        fontSize: 12,
        color: StandardPDFColors.descriptionTextGray,
      }}
    >
      {content}
    </Html>
  </View>
);

const Field = ({ content, title }: FieldInformation) => (
  <View
    style={{
      marginTop: 20,
      gap: '8px',
      flexGrow: 1,
      justifyContent: 'flex-start',
    }}
  >
    <Text style={{ ...styles.sizeBody, ...styles.medium }}>{title}</Text>
    <Text
      style={{
        fontSize: 12,
        color: StandardPDFColors.descriptionTextGray,
      }}
    >
      {content}
    </Text>
  </View>
);

const TextDescription = ({ title }: ReportTitleProps) => (
  <View style={{ marginTop: 20, gap: '8px' }} wrap={true}>
    <Html
      stylesheet={formattedTextStyles}
      style={{
        fontSize: 12,
        color: StandardPDFColors.descriptionTextGray,
      }}
    >
      {title}
    </Html>
  </View>
);

const FieldTitle = ({ title }: ReportTitleProps) => (
  <View style={{ marginTop: 40, gap: '8px' }} wrap={false}>
    <Text
      style={{
        ...styles.sizeHeading,
        ...styles.medium,
      }}
    >
      {title}
    </Text>
  </View>
);

const ReportTitle = ({ title }: ReportTitleProps) => {
  const intl = getCurrentIntl();
  return (
    <View style={documentStyles.reportTitle}>
      <Text
        style={{
          ...styles.bold,
          ...styles.sizeHeading,
        }}
      >
        {title}
      </Text>
      <Text
        style={{
          ...styles.sizeSubHeading,
        }}
      >
        {intl.formatMessage({ defaultMessage: 'Final Report' })}
      </Text>
    </View>
  );
};

const CustomFields = ({ fields }: ReportCustomFields) => (
  <View>
    {fields.map((field, index) => {
      switch (field.type) {
        case fieldTypes.title:
          return <FieldTitle key={index} title={field.title} />;
        case fieldTypes.descriptionText:
          return <TextDescription key={index} title={field.title} />;
        case fieldTypes.bigTextField:
          return (
            <BigTextField
              key={index}
              title={field.title}
              content={field.value}
            />
          );
        default:
          return (
            <View
              style={documentStyles.reportCustomFieldSiblings}
              wrap={false}
              key={index}
            >
              <Field title={field.title} content={field.value} key={index} />
              {field.siblings &&
                field.siblings.map((sibling, siblingIndex) => (
                  <Field
                    key={siblingIndex}
                    title={sibling.title}
                    content={sibling.value}
                  />
                ))}
            </View>
          );
      }
    })}
  </View>
);

const getReportDocument = (details: ModifiedFinalReportDocument) => {
  return (
    <Document>
      <Page size='A4' style={styles.page} wrap>
        <Header />
        <View style={styles.main}>
          <ReportTitle title={details.title} />
          <ReportDetailsHeader />
          <ReportInformation
            assignee={details.assignee}
            reportType={details.reportType}
            reportReference={details.referenceID}
            reportSeverity={details.priority}
            dateReceived={details.dateReceived}
          />
          <InformedIndividualSectionFactory
            hasInformedIndividuals={details.has_informed_individuals}
            pdf={true}
            items={details.informed_individuals}
          />
          <CustomFields fields={details.fieldValues} />
        </View>
        <Footer />
      </Page>
    </Document>
  );
};

const generateFinalReportPDF = async (data: ModifiedFinalReportDocument) => {
  const generatedPDF = getReportDocument(data);
  const pdfBlob = await pdf(generatedPDF).toBlob();
  return pdfBlob;
};

export { generateFinalReportPDF };
